import { EntityTypeEnum, Market, Permission, PlatformFeature } from 'api'
import { Translation } from 'locale'
import React from 'react'
import { initiatorAxeDetailsCountSelector, responderAxeDetailsCountSelector } from 'selectors/axeSelectors'
import { authorisedCollateralBasketNumberOfItemsSelector } from 'selectors/collateralBasketSelectors'
import { adminClearingAlertSelector, someDealsWithinWarningPeriodSelector } from 'selectors/dealSelectors'
import {
  historicalMidTradeEventsDetailsCountSelector,
  incomingMidTradeEventsDetailsCountSelector,
  initiatedMidTradeEventsDetailsCountSelector,
  MID_TRADE_VIEW_HISTORICAL,
  MID_TRADE_VIEW_INCOMING,
  MID_TRADE_VIEW_INITIATED,
  midTradeEventsDetailsCountSelector
} from 'selectors/midTradeEventsSelectors'
import { incomingNegotiationsNumberOfItemsSelector, initiatedNegotiationsNumberOfItemsSelector } from 'selectors/negotiations'
import { stagingOrdersNumberOfItemsSelector } from 'selectors/orderSelectors'
import { isBuySideTraderSelector, isSellSideTraderSelector } from 'selectors/permissionSelectors'
import { createPlatformFeatureEnabledSelector } from 'selectors/platformSelectors'
import { createGridActivityHighlightSelector } from 'selectors/selectionSelectors'
import { canViewControlLimitNavLinkSelector } from 'selectors/tradingControlSelectors'
import { GridId } from 'store/grid'
import { leftNavigationIconSet } from 'view/shared/leftNavigation'
import { ClearingHouseScreen } from './admin/clearingHouse'
import { ClientLogStreamScreen } from './admin/clientLogStream'
import { CollateralScreen, CollateralToolbar } from './admin/collateral'
import { AdminCollateralBasketScreen, AdminCollateralBasketToolbar } from './admin/collateralBasket'
import { FatFingerScreen, FatFingerToolbar } from './admin/fatFinger'
import { FxRateScreen } from './admin/fxRate'
import { HolidayScreen, HolidayToolbar } from './admin/holiday'
import { IndexScreen, IndexToolbar } from './admin/index'
import { OrderRecordKeepingScreen } from './admin/orderRecordKeeping'
import { OrganisationScreen, OrganisationToolbar } from './admin/organisation'
import { PlatformScreen } from './admin/platform'
import { ProductScreen, ProductToolbar } from './admin/product'
import { SectorScreen, SectorToolbar, SectorMappingScreen } from './admin/sector'
import { TradingControlScreen, TradingControlToolbar } from './admin/tradingControl'
import { TradingEntityScreen, TradingEntityToolbar } from './admin/tradingEntity'
import { UsersScreen, UsersToolbar } from './admin/users'
import { AuditScreen } from './audit'
import { AxeBoardInitiator, AxeBoardResponder, AxeBoardToolbar } from './axeBoard'
import { CollateralBasketScreen as BasketScreen } from './collateralBasket'
import { DealsScreen, RollsDealsScreen } from './deals'
import { HaircutScreen, HaircutToolbar } from './haircut'
import { MidTradeEventsScreen, MidTradeEventsToolbar } from './midTradeEvents'
import { OpenReposScreen } from './openRepos'
import { StructuredScreen } from './structured'
import { PrimeBrokerDefaultsScreen, PrimeBrokerDefaultsToolbar } from './primeBrokerDefaults'
import { CompletedNegotiationsScreen } from './requests/completed'
import { IncomingNegotiationsLeftNavigation, IncomingNegotiationsScreen, IncomingNegotiationsToolbar } from './requests/incoming'
import { curveNegotiationsTotalNumberOfCuvesSelector } from './requests/incoming/curves/CurveNegotiationSelectors'
import { CurveNegotiationsLeftNavigation } from './requests/incoming/curves/CurveNegotiationsLeftNavigation'
import { CurveNegotiationsScreen } from './requests/incoming/curves/CurveNegotiationsScreen'
import { CurveNegotiationsToolbar } from './requests/incoming/curves/CurveNegotiationsToolbar'
import { InitiatedNegotiationsLeftNavigation, InitiatedNegotiationsScreen } from './requests/initiated'
import { ResponderSalesCoverageScreen } from './responderSalesCoverage'
import { ResponderSalesCoverageToolbar } from './responderSalesCoverage/ResponderSalesCoverageToolbar'
import { RouteConfig } from './routeConfig'
import { SalesCoverageScreen } from './salesCoverage'
import { StagingLeftNavigation, StagingScreen, StagingToolbar } from './staging'
import { TradeSplitScreen, TradeSplitToolbar } from './tradeSplit'
import { TradingEntityAliasesScreen } from './tradingEntityAliases'
import { TradingEntityAliasesToolbar } from './tradingEntityAliases/TradingEntityAliasesToolbar'
import { TradingEntityDefaultsScreen } from './tradingEntityDefaults'
import { IssuerOrganisationsScreen, IssuerOrganisationsToolbar } from './admin/issuerOrganisations/IssuerOrganisationsScreen'
import { TradingLimitsScreen } from './tradingLimits'
import { isBuySideTradeAndHasLimitTypeSelector } from 'selectors/userSelectors'
import { canViewOrEditSectorMappingSelector } from 'selectors/productSelectors'
import { InitiatedGridViewScreen } from './requests/initiatedGridView'
import { TradingFatFingerLimitsScreen } from './tradingFatFingerLimits/TradingFatFingerLimitsScreen'
import { shouldShowGridViewTabSelector } from 'selectors/organisationSelectors'

export const mainRoutes: RouteConfig[] = [
  {
    url: '/staging',
    icon: leftNavigationIconSet.stagingIcon,
    permissions: [Permission.STAGING_VIEW],
    caption: Translation.TC_SECTION_STAGING,
    content: <StagingScreen />,
    toolbar: <StagingToolbar />,
    isDefault: true,
    itemCountSelector: stagingOrdersNumberOfItemsSelector,
    renderer: routeConfig => <StagingLeftNavigation routeConfig={routeConfig} />,
    activeEntitySubscriptions: [EntityTypeEnum.ORDER]
  },
  {
    url: '/initiated',
    icon: leftNavigationIconSet.initiatedIcon,
    permissions: [Permission.INITIATED_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.NEGOTIATION],
    caption: Translation.TC_SECTION_INITIATED,
    content: <InitiatedNegotiationsScreen />,
    itemCountSelector: initiatedNegotiationsNumberOfItemsSelector,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.InitiatedGrid),
    renderer: routeConfig => <InitiatedNegotiationsLeftNavigation routeConfig={routeConfig} />
  },
  {
    url: '/initiatedGridView',
    icon: leftNavigationIconSet.gridViewIcon,
    permissions: [Permission.INITIATED_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.NEGOTIATION, EntityTypeEnum.NEGOTIATION_HISTORY],
    caption: Translation.TC_SECTION_INITIATED_GRID_VIEW,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.GridView),
    content: <InitiatedGridViewScreen />,
    visibleSelector: shouldShowGridViewTabSelector
  },
  {
    url: '/incoming',
    icon: leftNavigationIconSet.incomingIcon,
    permissions: [Permission.INCOMING_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.NEGOTIATION, EntityTypeEnum.NEGOTIATION_HISTORY],
    caption: Translation.TC_SECTION_INCOMING,
    content: <IncomingNegotiationsScreen />,
    toolbar: <IncomingNegotiationsToolbar />,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.IncomingGrid),
    itemCountSelector: incomingNegotiationsNumberOfItemsSelector,
    renderer: routeConfig => <IncomingNegotiationsLeftNavigation routeConfig={routeConfig} />
  },
  {
    url: '/curves',
    icon: leftNavigationIconSet.incomingIcon,
    permissions: [Permission.INCOMING_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.NEGOTIATION, EntityTypeEnum.NEGOTIATION_HISTORY],
    caption: Translation.TC_SECTION_CURVES,
    content: <CurveNegotiationsScreen />,
    toolbar: <CurveNegotiationsToolbar />,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.CurvesGrid),
    itemCountSelector: curveNegotiationsTotalNumberOfCuvesSelector,
    renderer: routeConfig => <CurveNegotiationsLeftNavigation routeConfig={routeConfig} />,
    visibleSelector: createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_CURVE_COLLECTIONS)
  },
  {
    url: '/trades',
    icon: leftNavigationIconSet.tradesIcon,
    permissions: [Permission.TRADE_BLOTTER_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.DEAL, EntityTypeEnum.DEAL_HISTORY],
    caption: Translation.TC_SECTION_TRADES,
    content: <DealsScreen />,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.DealsGrid),
    alertHighlightSelector: adminClearingAlertSelector,
    warnHighlightSelector: someDealsWithinWarningPeriodSelector,
    childRoutes: [
      {
        url: '/openRepos',
        icon: leftNavigationIconSet.openReposIcons,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [EntityTypeEnum.DEAL, EntityTypeEnum.DEAL_HISTORY],
        caption: Translation.TC_SECTION_OPEN,
        content: <OpenReposScreen />
      },
      {
        url: '/rolls',
        icon: leftNavigationIconSet.openReposIcons,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [EntityTypeEnum.DEAL, EntityTypeEnum.DEAL_HISTORY],
        caption: Translation.TC_SECTION_ROLLS,
        content: <RollsDealsScreen />
      },
      {
        url: '/structured',
        icon: leftNavigationIconSet.openReposIcons,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [EntityTypeEnum.DEAL, EntityTypeEnum.DEAL_HISTORY],
        caption: Translation.TC_SECTION_STRUCTURED,
        content: <StructuredScreen />,
        visibleSelector: createPlatformFeatureEnabledSelector(PlatformFeature.ENABLE_EVERGREENS)
      }
    ]
  },
  {
    url: '/midTradeEvents',
    icon: leftNavigationIconSet.historicalIcon,
    permissions: [Permission.TRADE_BLOTTER_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.DEAL, EntityTypeEnum.DEAL_HISTORY, EntityTypeEnum.NEGOTIATION],
    caption: Translation.TC_SECTION_MID_POST_TRADE_EVENTS,
    content: <MidTradeEventsScreen />,
    itemCountSelector: midTradeEventsDetailsCountSelector,
    activityHighlightSelector: createGridActivityHighlightSelector(GridId.MidTradeEventsGrid),
    toolbar: <MidTradeEventsToolbar />,
    childRoutes: [
      {
        url: `/${MID_TRADE_VIEW_INITIATED}`,
        icon: leftNavigationIconSet.initiatedIcon,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_INITIATED,
        itemCountSelector: initiatedMidTradeEventsDetailsCountSelector,
        toolbar: <MidTradeEventsToolbar view={MID_TRADE_VIEW_INITIATED} />,
        content: <MidTradeEventsScreen view={MID_TRADE_VIEW_INITIATED} />
      },
      {
        url: `/${MID_TRADE_VIEW_INCOMING}`,
        icon: leftNavigationIconSet.incomingIcon,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_INCOMING,
        itemCountSelector: incomingMidTradeEventsDetailsCountSelector,
        toolbar: <MidTradeEventsToolbar view={MID_TRADE_VIEW_INCOMING} />,
        content: <MidTradeEventsScreen view={MID_TRADE_VIEW_INCOMING} />
      },
      {
        url: `/${MID_TRADE_VIEW_HISTORICAL}`,
        icon: leftNavigationIconSet.incomingIcon,
        permissions: [Permission.TRADE_BLOTTER_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_HISTORY,
        itemCountSelector: historicalMidTradeEventsDetailsCountSelector,
        toolbar: <MidTradeEventsToolbar view={MID_TRADE_VIEW_HISTORICAL} />,
        content: <MidTradeEventsScreen view={MID_TRADE_VIEW_HISTORICAL} />
      }
    ]
  },
  {
    url: '/axeboard',
    redirectTo: ['/axeboard/initiator', '/axeboard/incoming'],
    icon: leftNavigationIconSet.axeBoardIcon,
    permissions: [Permission.AXE_BOARD_RESPOND, Permission.AXE_BOARD_INITIATE, Permission.AXE_BOARD_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.AXE],
    caption: Translation.TC_SECTION_AXE_BOARD,
    childRoutes: [
      {
        url: `/incoming`,
        icon: leftNavigationIconSet.axeBoardIcon,
        permissions: [Permission.AXE_BOARD_RESPOND],
        activeEntitySubscriptions: [EntityTypeEnum.AXE],
        caption: Translation.TC_SECTION_AXE_BOARD_RESPONDER,
        itemCountSelector: responderAxeDetailsCountSelector,
        activityHighlightSelector: createGridActivityHighlightSelector(GridId.AxeGridResponder),
        content: <AxeBoardResponder />
      },
      {
        url: `/initiator`,
        icon: leftNavigationIconSet.axeBoardIcon,
        permissions: [Permission.AXE_BOARD_INITIATE, Permission.AXE_BOARD_VIEW],
        activeEntitySubscriptions: [EntityTypeEnum.AXE],
        caption: Translation.TC_SECTION_AXE_BOARD_INITIATOR,
        itemCountSelector: initiatorAxeDetailsCountSelector,
        activityHighlightSelector: createGridActivityHighlightSelector(GridId.AxeGridInitiator),
        toolbar: <AxeBoardToolbar />,
        content: <AxeBoardInitiator />
      }
    ]
  },
  {
    url: '/baskets',
    icon: leftNavigationIconSet.basketIcon,
    permissions: [Permission.STAGING_MANAGE],
    activeEntitySubscriptions: [],
    caption: Translation.TC_SECTION_COLLATERAL_BASKETS,
    content: <BasketScreen />,
    itemCountSelector: authorisedCollateralBasketNumberOfItemsSelector
  },
  {
    url: '/history',
    icon: leftNavigationIconSet.historyIcon,
    permissions: [Permission.INCOMING_RFQS_VIEW, Permission.INITIATED_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.DEAL_HISTORY, EntityTypeEnum.NEGOTIATION_HISTORY],
    caption: Translation.TC_SECTION_NEGOTIATION_HISTORY,
    content: <AuditScreen />
  },
  {
    url: '/historical',
    icon: leftNavigationIconSet.historicalIcon,
    permissions: [Permission.INITIATED_RFQS_VIEW, Permission.INCOMING_RFQS_VIEW],
    activeEntitySubscriptions: [EntityTypeEnum.DEAL_HISTORY, EntityTypeEnum.NEGOTIATION_HISTORY],
    caption: Translation.TC_SECTION_HISTORICAL,
    content: <CompletedNegotiationsScreen />
  },
  {
    url: '/admin',
    icon: leftNavigationIconSet.adminIcon,
    permissions: [
      Permission.USERS_VIEW,
      Permission.USERS_MANAGE,
      Permission.HAIRCUT_VIEW,
      Permission.ORGANISATION_META_VIEW,
      Permission.TRADING_ENTITY_MAPPING_VIEW,
      Permission.ADMIN_MENU
    ],
    activeEntitySubscriptions: [],
    caption: Translation.TC_SECTION_ADMIN,
    childRoutes: [
      {
        url: '/tradingEntityDefaults',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADING_ENTITY_DEFAULTS,
        content: <TradingEntityDefaultsScreen />,
        visibleSelector: isSellSideTraderSelector
      },
      {
        url: '/tradingEntityAliases',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADING_ENTITY_ALIASES,
        content: <TradingEntityAliasesScreen />,
        toolbar: <TradingEntityAliasesToolbar />,
        visibleSelector: isBuySideTraderSelector
      },
      {
        url: '/primeBrokerDefaults',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_PRIME_BROKER_DEFAULTS,
        content: <PrimeBrokerDefaultsScreen />,
        toolbar: <PrimeBrokerDefaultsToolbar />
      },
      {
        url: '/salesCoverage',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_SALES_COVERAGE,
        content: <SalesCoverageScreen />,
        visibleSelector: isBuySideTraderSelector
      },
      {
        url: '/tradingLimits',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADING_LIMITS,
        content: <TradingLimitsScreen />,
        visibleSelector: isBuySideTradeAndHasLimitTypeSelector
      },
      {
        url: '/fatFingerTradingLimits',
        permissions: [Permission.TRADING_ENTITY_MAPPING_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_FAT_FINGER_LIMITS,
        content: <TradingFatFingerLimitsScreen />,
        visibleSelector: isBuySideTraderSelector
      },
      {
        url: '/responderSalesCoverage',
        permissions: [Permission.ORGANISATION_META_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_RESPONDER_SALES_COVERAGE,
        toolbar: <ResponderSalesCoverageToolbar />,
        content: <ResponderSalesCoverageScreen />,
        visibleSelector: isSellSideTraderSelector
      },
      {
        url: '/haircut',
        permissions: [Permission.HAIRCUT_MANAGE, Permission.HAIRCUT_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_HAIRCUT,
        toolbar: <HaircutToolbar />,
        content: <HaircutScreen />
      },
      {
        url: '/tradeSplit',
        permissions: [Permission.ORGANISATION_META_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADE_SPLITS,
        toolbar: <TradeSplitToolbar />,
        content: <TradeSplitScreen />,
        visibleSelector: isBuySideTraderSelector
      },
      {
        url: '/tradingControl',
        permissions: [Permission.TRADING_CONTROL_LIMIT_MANAGE, Permission.TRADING_CONTROL_LIMIT_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADING_CONTROL,
        toolbar: <TradingControlToolbar />,
        content: <TradingControlScreen />,
        visibleSelector: canViewControlLimitNavLinkSelector
      },
      {
        url: '/platform',
        permissions: [Permission.PLATFORM_TRADING_SUSPEND, Permission.PLATFORM_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_PLATFORM,
        content: <PlatformScreen />,
        childRoutes: [
          {
            url: `/clientLogging`,
            permissions: [Permission.UPDATE_CLIENT_LOGGING],
            activeEntitySubscriptions: [EntityTypeEnum.PLATFORM],
            caption: Translation.TC_SECTION_PLATFORM_CLIENT_LOGSTREAMS,
            toolbar: <></>,
            content: <ClientLogStreamScreen />
          }
        ]
      },
      {
        url: '/compliance',
        permissions: [Permission.ISSUER_ORGANISATION_MANAGE, Permission.ISSUER_ORGANISATION_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_COMPLIANCE,
        content: <></>,
        childRoutes: [
          {
            url: '/issuerOrganisations',
            permissions: [Permission.ISSUER_ORGANISATION_MANAGE, Permission.ISSUER_ORGANISATION_VIEW],
            activeEntitySubscriptions: [],
            caption: Translation.TC_SECTION_COMPLIANCE_ISSUER_ORGANISATIONS,
            content: <IssuerOrganisationsScreen />,
            toolbar: <IssuerOrganisationsToolbar />
          }
        ]
      },
      {
        url: '/orderRecordKeeping',
        permissions: [Permission.REPORTING_TRIGGERS_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_ORDER_RECORD_KEEPING,
        content: <OrderRecordKeepingScreen />
      },
      {
        url: '/organisations',
        permissions: [Permission.ORGANISATION_VIEW, Permission.ORGANISATION_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_ORGANISATIONS,
        toolbar: <OrganisationToolbar />,
        content: <OrganisationScreen />
      },
      {
        url: '/entities',
        permissions: [Permission.TRADING_ENTITY_VIEW, Permission.TRADING_ENTITY_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_TRADING_ENTITIES,
        toolbar: <TradingEntityToolbar />,
        content: <TradingEntityScreen />
      },
      {
        url: '/clearingHouses',
        permissions: [Permission.CLEARING_HOUSE_MANAGE, Permission.CLEARING_HOUSE_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_CLEARING_HOUSES,
        content: <ClearingHouseScreen />
      },
      {
        url: '/users',
        permissions: [Permission.USERS_VIEW, Permission.USERS_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_USERS,
        toolbar: <UsersToolbar />,
        content: <UsersScreen />
      },
      {
        url: '/products',
        permissions: [Permission.COLLATERAL_VIEW, Permission.COLLATERAL_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_COLLATERALS,
        toolbar: <CollateralToolbar />,
        content: <CollateralScreen />,
        childRoutes: [
          {
            url: `/${Market.EUROPE}`,
            permissions: [Permission.COLLATERAL_VIEW, Permission.COLLATERAL_MANAGE],
            activeEntitySubscriptions: [],
            caption: Translation.TC_SECTION_EU_MARKET,
            toolbar: <CollateralToolbar />,
            content: <CollateralScreen market={Market.EUROPE} />
          },
          {
            url: `/${Market.US}`,
            permissions: [Permission.COLLATERAL_VIEW, Permission.COLLATERAL_MANAGE],
            activeEntitySubscriptions: [],
            caption: Translation.TC_SECTION_US_MARKET,
            toolbar: <CollateralToolbar />,
            content: <CollateralScreen market={Market.US} />
          }
        ]
      },
      {
        url: '/baskets',
        permissions: [Permission.COLLATERAL_BASKET_VIEW, Permission.COLLATERAL_BASKET_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_COLLATERAL_BASKETS,
        toolbar: <AdminCollateralBasketToolbar />,
        content: <AdminCollateralBasketScreen />
      },
      {
        url: '/fatFingers',
        permissions: [Permission.FAT_FINGER_LIMITS_VIEW, Permission.FAT_FINGER_LIMITS_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_FAT_FINGER_LIMITS,
        toolbar: <FatFingerToolbar />,
        content: <FatFingerScreen />
      },
      {
        url: '/holidays',
        permissions: [Permission.HOLIDAY_MANAGE, Permission.HOLIDAY_VIEW],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_HOLIDAYS,
        toolbar: <HolidayToolbar />,
        content: <HolidayScreen />
      },
      {
        url: '/indices',
        permissions: [Permission.INDEX_VIEW, Permission.INDEX_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_INDICES,
        toolbar: <IndexToolbar />,
        content: <IndexScreen />
      },
      {
        url: '/product',
        permissions: [Permission.PRODUCT_VIEW, Permission.PRODUCT_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_PRODUCTS,
        toolbar: <ProductToolbar />,
        content: <ProductScreen />
      },
      {
        url: '/sector',
        permissions: [Permission.SECTOR_VIEW, Permission.SECTOR_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_SECTORS,
        toolbar: <SectorToolbar />,
        content: <SectorScreen />,
        childRoutes: [
          {
            url: '/mapping',
            permissions: [],
            visibleSelector: canViewOrEditSectorMappingSelector,
            activeEntitySubscriptions: [],
            caption: Translation.TC_SECTION_SECTOR_MAPPING,
            content: <SectorMappingScreen />
          }
        ]
      },
      {
        url: '/fxRates',
        permissions: [Permission.FX_RATE_VIEW, Permission.FX_RATE_MANAGE],
        activeEntitySubscriptions: [],
        caption: Translation.TC_SECTION_FX_RATES,
        content: <FxRateScreen />
      }
    ]
  }
]
